import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrencies, setCurrenciesLoading, setCurrenciesError } from "../redux/actions/dashboard";
import billingService from "../services/billingService";
import logger from "../utils/logger";


/**
 * Custom hook to manage currencies data with Redux caching
 * - Loads currencies only once per session
 * - Caches data in Redux store
 * - Automatically cleared on logout
 */
export const useCurrencies = () => {
  const dispatch = useDispatch();
  const { currencies, currenciesLoading, currenciesError } = useSelector(state => ({
    currencies: state.dashboard?.currencies || [],
    currenciesLoading: state.dashboard?.currenciesLoading || false,
    currenciesError: state.dashboard?.currenciesError || null,
  }));

  const loadCurrencies = async () => {
    // If currencies are already loaded, don't fetch again
    if (currencies.length > 0) {
      return currencies;
    }

    // If already loading, don't start another request
    if (currenciesLoading) {
      return currencies;
    }

    try {
      dispatch(setCurrenciesLoading(true));
      dispatch(setCurrenciesError(null));

      const response = await billingService.getCurrencies({ limit: 500 });

      if (response && response.data && response.data.data) {
        dispatch(setCurrencies(response.data.data));
        return response.data.data;
      } else {
        throw new Error("No currencies data received");
      }
    } catch (error) {
      logger.error("Error loading currencies:", error);
      dispatch(setCurrenciesError(error.message || "Failed to load currencies"));
      return [];
    }
  };

  // Auto-load currencies when hook is first used (only if authenticated)
  useEffect(() => {
    const hasToken = !!(localStorage.getItem("authToken") || localStorage.getItem("access_token"));
    if (hasToken && currencies.length === 0 && !currenciesLoading && !currenciesError) {
      loadCurrencies();
    }
  }, []);

  return {
    currencies,
    currenciesLoading,
    currenciesError,
    loadCurrencies,
    hasCurrencies: currencies.length > 0,
  };
};
